import React, { useMemo, useRef, useState } from "react";
import { ChevronDown, Leaf, Send, X } from "lucide-react";
import { api, API_BASE, inr } from "@/lib/api";

const sessionKey = "eira-support-chat-session";

const getSessionId = () => {
  const existing = sessionStorage.getItem(sessionKey);
  if (existing) return existing;
  const id = `eira_${crypto.randomUUID()}`;
  sessionStorage.setItem(sessionKey, id);
  return id;
};

const cleanAssistantMessage = (content) => String(content || "")
  .replace(/\*\*/g, "")
  .replace(/`/g, "")
  .replace(/^\s*-\s+/gm, "• ");

const START_ACTIONS = [
  ["track-orders", "Track Order"],
  ["return-refund", "Return / Refund"],
  ["points", "Eira Points"],
  ["products", "Product Info"],
  ["delivery", "Delivery Info"],
  ["coupons", "Coupons"],
  ["support", "Talk to Support"],
];

export const EiraChatWidget = ({ raisedForConsent = false }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content: "Hi, I’m Eira. I can help with orders, products, delivery, coupons, and Eira Points.",
    },
  ]);
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState("");
  const [actions, setActions] = useState(START_ACTIONS);
  const [inputMode, setInputMode] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useMemo(getSessionId, []);

  const scrollToLatest = () => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  };

  const sendMessage = async () => {
    const message = input.trim();
    if (!message || streaming) return;
    if (inputMode === "ticket") {
      setActionLoading(true);
      try {
        await api.post("/support/tickets", {
          message,
          session_id: sessionId,
          category: selectedOrder ? "order" : "other",
          order_id: selectedOrder?.id,
        });
        setMessages((current) => [...current, { id: crypto.randomUUID(), role: "customer", content: message }, {
          id: crypto.randomUUID(), role: "assistant", content: "Your support request is with our team. We’ll get back to you soon.",
        }]);
        setActions([["start", "Main menu"]]);
        setInputMode("");
        setInput("");
      } catch {
        setStatus("I couldn’t create the ticket. Please try again.");
      } finally {
        setActionLoading(false);
      }
      return;
    }
    const userMessage = { id: crypto.randomUUID(), role: "customer", content: message };
    const assistantId = crypto.randomUUID();
    setMessages((current) => [...current, userMessage, { id: assistantId, role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    setStatus("Eira is checking…");
    scrollToLatest();
    try {
      const response = await fetch(`${API_BASE}/support/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message }),
      });
      if (!response.ok || !response.body) throw new Error("Eira is temporarily unavailable.");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        events.forEach((chunk) => {
          const eventName = chunk.match(/^event: (.+)$/m)?.[1];
          const rawData = chunk.match(/^data: (.+)$/m)?.[1];
          if (!rawData) return;
          const data = JSON.parse(rawData);
          if (eventName === "delta") {
            setMessages((current) => current.map((item) => (
              item.id === assistantId ? { ...item, content: item.content + data.text } : item
            )));
            scrollToLatest();
          }
          if (eventName === "status") setStatus("Eira is checking details…");
          if (eventName === "done") setStatus("");
        });
      }
    } catch (error) {
      setMessages((current) => current.map((item) => (
        item.id === assistantId
          ? { ...item, content: "I’m sorry, I couldn’t connect right now. Please try again shortly." }
          : item
      )));
      setStatus("");
    } finally {
      setStreaming(false);
      scrollToLatest();
    }
  };

  const addAssistant = (content, nextActions = []) => {
    setMessages((current) => [...current, { id: crypto.randomUUID(), role: "assistant", content }]);
    setActions(nextActions);
    setInputMode("");
    scrollToLatest();
  };

  const chooseAction = async ([id, label, data]) => {
    if (id === "start") {
      addAssistant("What can I help you with?", START_ACTIONS);
      return;
    }
    if (id === "question") {
      setInputMode("question");
      setActions([]);
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    if (id === "support") {
      addAssistant("Please tell me briefly what you need help with, and I’ll send it to our support team.");
      setInputMode("ticket");
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    if (id === "delivery") {
      addAssistant("Delivery takes 3–7 business days. Shipping is free over ₹499; otherwise it is ₹49.", [
        ["track-orders", "Track Order"], ["return-refund", "Return / Refund"], ["start", "Main menu"],
      ]);
      return;
    }
    if (id === "return-refund") {
      addAssistant("Unopened items in original packaging can be requested within 7 days of delivery. Approved refunds take 5–7 working days.", [
        ["track-orders", "Choose an order"], ["support", "Talk to Support"], ["start", "Main menu"],
      ]);
      return;
    }
    setActionLoading(true);
    try {
      if (id === "track-orders") {
        const response = await api.get("/support/guided/recent-orders");
        if (!response.data.length) addAssistant("I couldn’t find recent orders for this signed-in account.", [["question", "Ask a question"], ["start", "Main menu"]]);
        else addAssistant("Tap an order to see its latest status.", response.data.map((order) => ["order", `${order.reference} · ${order.order_status}`, order]));
      } else if (id === "order") {
        const response = await api.get(`/support/guided/orders/${data.id}`);
        const order = response.data;
        setSelectedOrder(data);
        addAssistant(`Order ${order.reference}: ${order.order_status}. Payment: ${order.payment_status}.${order.tracking_number ? ` Tracking: ${order.tracking_number}.` : ""}`, [["return-refund", "Return / Refund"], ["support", "Talk to Support"], ["start", "Main menu"]]);
      } else if (id === "points") {
        const response = await api.get("/support/guided/points");
        addAssistant(`You have ${inr(response.data.available_points)} available and ${inr(response.data.pending_points)} pending Eira Points. Use them in Checkout.`, [["question", "Ask about points"], ["start", "Main menu"]]);
      } else if (id === "products") {
        const response = await api.get("/support/guided/products");
        addAssistant("Choose a product to see its ingredients and details.", response.data.map((product) => ["product", product.name, product]));
      } else if (id === "product") {
        const product = data;
        addAssistant(`${product.name}: ${product.ingredients || product.short_description || product.description || "Product details are available on its product page."}`, [["products", "Choose another product"], ["start", "Main menu"]]);
      } else if (id === "coupons") {
        const response = await api.get("/support/guided/coupons");
        const codes = response.data.map((coupon) => coupon.code).filter(Boolean).slice(0, 4);
        addAssistant(`${codes.length ? `Available codes: ${codes.join(", ")}. ` : ""}Apply a code in Checkout or open Available Offers, then tap Apply.`, [["question", "Ask about a coupon"], ["start", "Main menu"]]);
      }
    } catch (error) {
      if (error?.response?.status === 401) addAssistant("Please sign in to view your personal orders or Eira Points.", [["question", "Ask a question"], ["start", "Main menu"]]);
      else addAssistant("I couldn’t load that right now. Please try again or contact support.", [["support", "Talk to Support"], ["start", "Main menu"]]);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div
      className={`fixed right-3 z-[70] sm:right-5 ${
        raisedForConsent ? "bottom-32 sm:bottom-28" : "bottom-3 sm:bottom-5"
      }`}
      data-testid="eira-chat-widget"
    >
      {open && (
        <section
          className="mb-2 flex h-[min(400px,calc(100vh-5.5rem))] w-[min(290px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-lg border border-[#D9C3A5] bg-[#FCFAF5] shadow-[0_14px_36px_rgba(47,66,54,0.2)] sm:w-[300px]"
          data-testid="eira-chat-panel"
        >
          <header className="flex items-center justify-between bg-[#1E2A24] px-3 py-2.5 text-white">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-[#C86D42] shadow-sm"><Leaf size={14} /></span>
              <div>
                <p className="font-serif text-base leading-none">Eira</p>
                <p className="mt-1 text-[8px] uppercase tracking-[0.16em] text-white/65">Eira Products support</p>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 hover:bg-white/10" data-testid="eira-chat-close-button" aria-label="Close Eira chat">
              <X size={16} />
            </button>
          </header>
          <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-3 py-3" data-testid="eira-chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={`max-w-[90%] rounded-md px-2.5 py-2 text-[13px] leading-5 ${
                message.role === "customer" ? "ml-auto bg-[#2D5A47] text-white shadow-sm" : "border border-[#E7E0D5] bg-white text-[#1E2A24]"
              }`} data-testid={`eira-chat-message-${message.role}`}>
                {message.role === "assistant" ? cleanAssistantMessage(message.content) : message.content || "…"}
              </div>
            ))}
            {actionLoading && <p className="text-[11px] text-[#8C6D46]" data-testid="eira-guided-loading">Eira is checking…</p>}
          </div>
          <div className="border-t border-[#EEE7DB] bg-[#FCFAF5] px-3 py-2">
            <div className="flex flex-wrap gap-1.5" data-testid="eira-guided-actions">
              {actions.map((action) => <button key={`${action[0]}-${action[1]}`} type="button" onClick={() => chooseAction(action)} disabled={actionLoading} className="rounded-full border border-[#D9C3A5] bg-white px-2 py-1 text-[10px] font-semibold text-[#2D5A47] disabled:opacity-40" data-testid={`eira-action-${action[0]}`}>{action[1]}</button>)}
              {!actions.length && !inputMode && <button type="button" onClick={() => chooseAction(["question", "Ask a question"])} className="rounded-full border border-[#D9C3A5] bg-white px-2 py-1 text-[10px] font-semibold text-[#2D5A47]" data-testid="eira-free-question">Ask a question</button>}
            </div>
          </div>
          {status && <p className="px-3 pb-1 text-[11px] text-[#8C6D46]" data-testid="eira-chat-status">{status}</p>}
          {inputMode && <form onSubmit={(event) => { event.preventDefault(); sendMessage(); }} className="flex gap-2 border-t border-[#E7E0D5] bg-white p-2.5">
            <input ref={inputRef} value={input} onChange={(event) => setInput(event.target.value)} placeholder={inputMode === "ticket" ? "Describe your support issue…" : "Ask Eira…"} className="min-w-0 flex-1 rounded-md border border-[#DED4C5] px-2.5 py-2 text-base outline-none focus:border-[#2D5A47] sm:text-[13px]" data-testid="eira-chat-input" />
            <button type="submit" disabled={!input.trim() || streaming || actionLoading} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C86D42] text-white shadow-sm disabled:opacity-40" data-testid="eira-chat-send-button" aria-label="Send message to Eira"><Send size={14} /></button>
          </form>}
        </section>
      )}
      <button type="button" onClick={() => setOpen((value) => !value)} className="flex h-11 items-center gap-2 rounded-full border border-[#D9C3A5] bg-[#1E2A24] px-3 text-xs font-semibold text-white shadow-[0_8px_20px_rgba(47,66,54,0.24)] hover:bg-[#2D5A47]" data-testid="eira-chat-toggle" aria-expanded={open}>
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#C86D42]"><Leaf size={12} /></span>
        {open ? <ChevronDown size={15} /> : "Ask Eira"}
      </button>
    </div>
  );
};